import { action, observable, reaction, runInAction, makeObservable } from "mobx";
import S3FileUpload from "./S3FileUpload";
const isSSR = (typeof window === "undefined")

const GB = 1073741824;

/** Number of files that are uploaded at the same time */
const FILE_CONCURRENCY = 3;

/** Interval to check if the upload worker has processed all files */
const PROCESSING_CHECK_INTERVAL = 1000;

/**
 * @callback promiseCallback
 * @returns {Promise}
 */
export default class UploadStore
{
  STATE_ERROR = -1;
  STATE_WAITING = 0;
  STATE_IN_PROGRESS = 1;
  STATE_PROCESSING = 2
  STATE_COMPLETE = 3;

  user;

  /** @private */
  resolve;

  /** @private */
  reject;

  /**
   * @private
   */
  _cancelled = false;

  /**
   * @private
   */
  _paused = false;

  /**
   * @private
   */
  _onProgress;

  /**
   * @private
   */
  _onComplete;

  /**
   * @private
   */
  _onStatus;

  /**
   * @private
   */
  consecutiveFails = 0;

  /**
   * @private
   */
  maxConsecutiveFails = 5;

  /** @type {Set<UploadFile>} */
  files = new Set();

  /**
   * @private
   * @type {import("./S3FileUpload").S3UploadConfig}
   */
  s3Config;

  /** @private */
  uploadParams = new Map();

  /**
   * @type {Set<UploadFile>}
   */
  skippedFiles = new Set();

  /** @private */
  _data = new Map();

  /** @private */
  _debugInfo = [];

  state = this.STATE_WAITING;

  constructor() {
    makeObservable(this, {
      state: observable,
      files: observable,
      addFiles: action,
      start: action,
      cancel: action,
      clear: action,
    });

    if(!isSSR)
    {
      reaction(
        () => this.state,
        () => {
          // https://web.dev/bfcache/
          // unload events beinvloeden back/forward cache, dus alleen dit event toevoegen
          // als het ook echt nodig is
          if(this.state === this.STATE_IN_PROGRESS || this.state === this.STATE_ERROR)
          {
            window.addEventListener('beforeunload', this.watchForPageLeave, {capture: true});
          }else
          {
            window.removeEventListener('beforeunload', this.watchForPageLeave, {capture: true});
          }
        }
      )
    }
  }

  watchForPageLeave = (e) => {
    // Notify when leaving while upload (with error) is active
    if(this.state === this.STATE_IN_PROGRESS || this.state === this.STATE_ERROR)
    {
      e.preventDefault();
      e.returnValue = "";
      return "";
    }
  }

  setUser(user)
  {
    this.user = user;
  }

  /**
   * Set where to upload the files to (`s3` of the `/upload/init` response). The files are uploaded directly to S3 and
   * processed by the upload worker.
   * @param {import("./S3FileUpload").S3UploadConfig} config
   */
  setS3Config(config)
  {
    this.s3Config = config;
  }

  /**
   * Add files to the queue
   * @param {FileList} files
   */
  addFiles(files)
  {
    if(this.state === this.STATE_WAITING)
    {
      Array.from(files).forEach(file => this.files.add(new UploadFile(file)));
    }else
      throw new Error(`UploadStore is not ready, current state: ${this.state}`);
  }

  /**
   * Set a post param. Currently only supports 'uploadkey'
   * @param {'uploadkey'} key
   * @param {String} value
   */
  addParam(key, value)
  {
    this.uploadParams.set(key, value);
  }

  /**
   * Get a post param. Currently only supports 'uploadkey'
   * @param {'uploadkey'} key
   * @return {String}
   */
  getParam(key)
  {
    return this.uploadParams.get(key);
  }

  /**
   * Return if the upload is still active
   * @returns {Boolean}
   */
  isActive()
  {
    return ((this.state === this.STATE_IN_PROGRESS) || (this.state === this.STATE_PROCESSING));
  }

  /**
   * Return if the upload has completed
   * @returns {Boolean}
   */
  isCompleted()
  {
    return ((this.state === this.STATE_COMPLETE) || (this.state === this.STATE_ERROR));
  }

  /**
   * Start the upload. Returns a promise that resolves when completed and rejects on any error.
   * @returns {Promise}
   */
  start()
  {
    this._paused = false;
    this._cancelled = false;
    this.setState(this.STATE_IN_PROGRESS);

    return new Promise((resolve, reject) =>
    {
      if(!this.user)
        return reject("User not set");
      if(this.files.length < 1)
        return reject("No files selected");

      new Promise((startResolve, startReject) =>
      {
        this.resolve = startResolve;
        this.reject = startReject;

        this.initUpload()
        .then(() =>
        {
          if(!this.s3Config)
            throw new Error("No S3 upload config, set it in onInitUpload");

          if(!this._cancelled)
            this.startUploads();
        })
        .catch(startReject);
      })
      .then(() =>
      {
        if(!this._cancelled && !this._paused)
        {
          runInAction(() =>
          {
            if(!this._cancelled)
              this.setState(this.STATE_PROCESSING);
          });

          if(this.skippedFiles.size === this.files.size) {
            reject({type: "ALL_SKIPPED"});
            return;
          }

          this.waitForProcessing()
          .then(() => this.uploadComplete())
          .then(() => {
            this.uploadStatus().then(() => {
              runInAction(() =>
              {
                if(!this._cancelled)
                  this.setState(this.STATE_COMPLETE);
              });

              if(!this._cancelled)
                resolve();
            })
            .catch(reject);
          })
          .catch(reject);
        }
      })
      .catch((e) =>
      {
        if(!this._cancelled)
        {
          this.cancel();
          this.setState(this.STATE_ERROR);
          reject(e);
        }
      });
    })
  }

  pause()
  {
    this._paused = true;
  }

  resume()
  {
    this._paused = false;
    this.startUploads();
  }

  /**
   * Cancels the upload, cancels all files, resets all callback functions and resets the state to the initial state (STATE_WAITING)
   */
  cancel()
  {
    this._cancelled = true;
    this.files.forEach(file => file.cancel());
    this.clear();
  }

  /**
   * Clears the files and resets the state to the initial state (STATE_WAITING)
   */
  clear()
  {
    this.files.clear();
    this.onInitUpload(null);
    this.onProgress(null);
    this.onComplete(null);
    this.onStatus(null);

    this.consecutiveFails = 0;
    this.skippedFiles.clear();
    this.uploadParams.clear();
    this.s3Config = undefined;
    this.setState(this.STATE_WAITING);
  }

  /**
   * Set data for later use
   * @param {string} key
   * @param {any} value
   */
  setData(key, value)
  {
    this._data.set(key, value);
  }

  /**
   * Returns the data when set otherwise returns undefined
   * @param {string} key
   */
  getData(key)
  {
    return this._data.get(key);
  }

  /**
   * Set the onInitUpload callback
   * @param {promiseCallback} callback
   */
  onInitUpload(callback)
  {
    this._onInitUpload = callback;
  }

  /**
   * Set the onProgress callback
   * @param {Function} callback
   */
  onProgress(callback)
  {
    this._onProgress = callback;
  }

  /**
   * Set the onComplete callback
   * @param {promiseCallback} callback
   */
  onComplete(callback)
  {
    this._onComplete = callback;
  }

  /**
   * Set the onComplete callback
   * @param {promiseCallback} callback
   */
  onStatus(callback)
  {
    this._onStatus = callback;
  }

  /**
   * Emits a propgress event when set
   * @private
   */
  emitProgress()
  {
    if(this._onProgress)
      this._onProgress();
  }

  /**
   * Set the upload state
   * @private
   */
  setState(state)
  {
    runInAction(() => {
      this.state = state;
    })
  }

  /**
   * Triggers the initUpload callback when set
   * @private
   */
  initUpload()
  {
    return new Promise((resolve, reject) =>
    {
      if(this._onInitUpload)
        this._onInitUpload().then(resolve).catch(reject);
      else
        resolve();
    });
  }

  /**
   * Starts uploading files until `FILE_CONCURRENCY` files are uploading
   * @private
   */
  startUploads()
  {
    const uploading = Array.from(this.files).filter(file => file.state === file.STATE_IN_PROGRESS).length;
    for(let i = uploading; i < FILE_CONCURRENCY; i++)
      this.uploadNextFile();
  }

  /**
   * Triggers the next file to upload. When there are no more remaining files to upload will resolve the 'start' promise
   * @private
   */
  uploadNextFile()
  {
    if(this._paused || this._cancelled)
      return;

    const file = Array.from(this.files).find(file => file.state === this.STATE_WAITING);
    if(file)
    {
      const size = file.getSize();
      if(size > 1 * GB) {
        file.skip();
        this.skippedFiles.add(file);
        this.uploadNextFile();

        return;
      }

      file.onProgress(() =>
      {
        this.emitProgress();
      });

      file.onRetry(e => {
        this._debugInfo.push({
          file: file.file,
          time: new Date(),
          state: "FILE_RETRY",
          data: e
        });
      });

      this._debugInfo.push({
        file: file.file,
        time: new Date(),
        state: "UPLOAD_START"
      });

      file.upload(this.s3Config, this.getParam("uploadkey"))
      .then(() =>
      {
        this._debugInfo.push({
          file: file.file,
          time: new Date(),
          state: "UPLOAD_COMPLETE"
        });

        this.consecutiveFails = 0;
        this.uploadNextFile();
      })
      .catch((e) =>
      {
        if(!this._cancelled)
        {
          if(this.consecutiveFails < this.maxConsecutiveFails)
          {
            this._debugInfo.push({
              file: file.file,
              time: new Date(),
              state: "UPLOAD_SKIP",
              data: e
            });

            file.skip();

            this.consecutiveFails++;
            this.skippedFiles.add(file);

            this.uploadNextFile();
          } else {
            this._debugInfo.push({
              file: file.file,
              time: new Date(),
              state: "UPLOAD_ERROR",
              data: e
            });

            this.reject({...e, ...{type: "consecutivefails"}}, file);
          }
        }
      })
    }
    else if(!Array.from(this.files).some(file => file.state === file.STATE_IN_PROGRESS))
    {
      this.resolve();
    }
  }

  /**
   * Waits until the upload worker has processed the uploaded files. Also tells the number of uploaded files, the
   * upload waits for that number of files.
   * @private
   * @returns {Promise}
   */
  waitForProcessing()
  {
    const uploadKey = this.getParam("uploadkey");
    const successCount = Array.from(this.files).filter(file => file.state === file.STATE_COMPLETE).length;

    return new Promise((resolve, reject) =>
    {
      const checkStatus = () =>
      {
        if(this._cancelled)
          return reject({type: "cancelled"});

        const url = `${process.env.SITE_URL}/api/v1/member/upload/${uploadKey}/status?fileUploadSuccessCount=${successCount}`;
        const [promise] = this.user.FetchJSON(url, {legacy: true});
        promise.then(({data}) =>
        {
          if(data.status === "complete")
            resolve();
          else
            setTimeout(checkStatus, PROCESSING_CHECK_INTERVAL);
        })
        .catch(reject);
      };

      checkStatus();
    });
  }

  /**
   * Trigger the onComplete callback when set
   * @private
   */
  uploadComplete()
  {
    return new Promise((resolve, reject) =>
    {
      if(this._onComplete)
        this._onComplete().then(resolve).catch(reject);
      else
        resolve();
    });
  }

  /**
   * Trigger the onStatus callback when set
   * @private
   */
  uploadStatus()
  {
    return new Promise((resolve, reject) => {
      if(this._onStatus)
        this._onStatus().then(resolve).catch(reject);
      else
        resolve();
    });
  }

  /**
   * Get the current progress
   * @returns {{files: Number, complete: Number, percentage: Number}}
   */
  get progress()
  {
    let bytesTotal = 0;
    let bytesLoaded = 0;

    let complete = 0;

    this.files.forEach(file =>
    {
      switch(file.state)
      {
        case file.STATE_ERROR: // Mark same as complete for progress
        case file.STATE_SKIPPED:
        case file.STATE_COMPLETE:
          complete++;
          bytesTotal += file.getSize();
          bytesLoaded += file.getSize();
        break;
        case file.STATE_WAITING:
          bytesTotal += file.getSize();
        break;
        case file.STATE_IN_PROGRESS:
          bytesTotal += file.getSize();
          bytesLoaded += file.loaded;
        break;
        default:
          console.warn("Invalid upload file state", file.state, file)
      }
    });

    return {
      files: this.files.size,
      complete,
      percentage: (bytesLoaded > 0) ? (bytesLoaded / bytesTotal * 100) : 0
    };
  }

  get debug() {
    return JSON.stringify(this._debugInfo);
  }
}

class UploadFile
{
  STATE_SKIPPED = -2;
  STATE_ERROR = -1;
  STATE_WAITING = 0;
  STATE_IN_PROGRESS = 1;
  STATE_COMPLETE = 2;

  /**
   * @type {File}
   */
  file;

  /**
   * @private
   * @type {Function}
   */
  _onProgress;

  /**
   * @private
   * @type {Function}
   */
  _onRetry;

  /**
   * @private
   * @type {S3FileUpload}
   */
  _upload;

  /**
   * @type {number}
   */
  state = this.STATE_WAITING;

  /**
   * @type {number}
   */
  loaded = 0;

  /**
   * Create an `UploadFile` from a file
   * @param {File} file
   */
  constructor(file)
  {
    this.file = file;
  }

  /**
   * @returns {Number}
   */
  getSize()
  {
    return this.file.size;
  }

  /**
   * Upload the file to S3
   * @param {import("./S3FileUpload").S3UploadConfig} s3Config
   * @param {String} uploadKey
   * @returns {Promise}
   */
  upload(s3Config, uploadKey)
  {
    this.state = this.STATE_IN_PROGRESS;

    this._upload = new S3FileUpload(this.file, s3Config, uploadKey);
    this._upload.onProgress(loaded =>
    {
      this.loaded = loaded;
      this.emitProgress();
    });
    this._upload.onRetry(e => {
      this.emitRetry(e);
    });

    return this._upload.start()
    .then(() =>
    {
      this.state = this.STATE_COMPLETE;
    });
  }

  /**
   * Cancel the upload
   */
  cancel()
  {
    if(this.state === this.STATE_IN_PROGRESS)
      this.state = this.STATE_ERROR;
    this._upload?.cancel();
  }

  /**
   * Skip this file
   */
  skip()
  {
    this.state = this.STATE_SKIPPED;
  }

  /**
   * Add a progress event callback
   * @param {Function} callback
   */
  onProgress(callback)
  {
    this._onProgress = callback;
  }

  /**
   * Add a retry event callback
   * @param {Function} callback
   */
  onRetry(callback) {
    this._onRetry = callback;
  }

  /**
   * Emit a progress event to the `UploadStore`
   * @private
   */
  emitProgress()
  {
    if(this._onProgress)
      this._onProgress();
  }

  emitRetry(e) {
    if(this._onRetry) {
      this._onRetry(e);
    }
  }
}
